import { getTheme } from 'web_v2/src/styles/theme';
import { getThemeConfig } from 'web_v2/src/styles/theme/config';

declare global {
    interface Window {
        isHavePwaPower: string;
        deferredPrompt: any;
        pwaurl: string;
        pwaConfig?: PWAManifestConfig;
    }
}

export interface ForceConfig {
    type: string;
    apkUrl: string;
}

export interface PWAConfig {
    pageMode: number; // 页面模式 1-4
    forceType: string;
    forceApkUrl: string;
}

export interface PWAManifestConfig {
    name: string;
    shortName: string;
    description: string;
    themeColor: string;
    backgroundColor: string;
    iconUrl: string;
    startUrl?: string;
    screenshots?: any[];
}

import { ref, onMounted, computed } from 'vue';
import { useAppStore } from 'store/modules/appStore';

// 添加一个全局标志位
let isIOSUpdating = false;

export function usePWA() {
    const isInstalling = ref(false);
    const installProgress = ref(0);
    const isFinished = ref(false);
    const canInstallPWA = ref(window.isHavePwaPower === '1');
    const pwaFailCount = ref(0);
    const isPWAInstalled = ref(false);

    // 监听 PWA 安装能力变化
    const setupPWAListener = () => {
        window.addEventListener('beforeinstallprompt', () => {
            canInstallPWA.value = true;
        });

        window.addEventListener('appinstalled', () => {
            isFinished.value = true;
            canInstallPWA.value = false;
        });
    };

    // PWA 安装动画
    const startInstallAnimation = () => {
        isInstalling.value = true;
        isFinished.value = false;

        const duration = 10000; // 10秒
        const startTime = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min((elapsed / duration) * 300, 100);
            installProgress.value = Math.floor(progress);

            if (progress < 100) {
                requestAnimationFrame(animate);
            } else {
                finishInstallAnimation();
            }
        };

        requestAnimationFrame(animate);
    };

    const finishInstallAnimation = () => {
        isInstalling.value = false;
        isFinished.value = true;
        installProgress.value = 100;
    };

    // 检查PWA是否已安装
    const checkPWAInstalled = () => {
        // 只检查 display-mode
        // if (window.matchMedia('(display-mode: standalone)').matches || window.matchMedia('(display-mode: fullscreen)').matches || (window.navigator as any).standalone) {
        //     isPWAInstalled.value = true;
        //     return true;
        // }

        if ((window.navigator as any).standalone) {
            isPWAInstalled.value = true;
            // PWA环境下检查orientation配置并自动更新
            // checkAndUpdateOrientation();
            return true;
        }

        isPWAInstalled.value = false;
        return false;
    };

    // 新增：检查并更新orientation配置
    // const checkAndUpdateOrientation = async () => {
    //     console.log('===============checkAndUpdateOrientation===============');
    //     try {
    //         // 等待PWA配置就绪
    //         let attempts = 0;
    //         const maxAttempts = 20; // 增加等待次数

    //         while (attempts < maxAttempts) {
    //             if (window.pwaConfig?.name && window.pwaConfig?.iconUrl) {
    //                 break;
    //             }
    //             await new Promise(resolve => setTimeout(resolve, 200));
    //             attempts++;
    //         }

    //         if (window.pwaConfig?.name && window.pwaConfig?.iconUrl) {
    //             console.log('PWA已安装，检查orientation配置...');

    //             // 检查localStorage中是否已经标记过orientation已更新为any
    //             const orientationUpdated = localStorage.getItem('pwa_orientation_any_updated');

    //             if (!orientationUpdated) {
    //                 console.log('检测到PWA需要更新orientation为any，开始无感知更新...');

    //                 // 静默更新manifest，确保orientation为any
    //                 const success = await updateManifest(window.pwaConfig);

    //                 if (success) {
    //                     // 标记已更新，避免重复更新
    //                     localStorage.setItem('pwa_orientation_any_updated', Date.now().toString());
    //                     console.log('PWA orientation已成功更新为any，现在支持任意屏幕方向');
    //                 }
    //             } else {
    //                 console.log('PWA orientation配置已是最新（支持any方向）');
    //             }
    //         }
    //     } catch (error) {
    //         console.error('检查orientation配置时出错:', error);
    //     }
    // };

    // 添加一个函数来处理 iOS 的自动更新
    const handleIOSAutoUpdate = () => {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (!isIOS || isIOSUpdating) return;

        // 创建一个 MutationObserver 来监听 window.pwaConfig 的变化
        const checkConfig = () => {
            if (isIOSUpdating) return; // 如果正在更新，直接返回
            if (window.pwaConfig?.name && window.pwaConfig?.iconUrl) {
                isIOSUpdating = true;
                console.log('iOS设备，检测到配置已就绪，自动更新manifest');
                updateManifest(window.pwaConfig)
                    .catch(console.error)
                    .finally(() => {
                        // 更新完成后，清除所有定时器
                        clearInterval(interval);
                        clearInterval(configInterval);
                        // 不再重置 isIOSUpdating，保持为 true
                    });
            }
        };

        // 首次检查
        checkConfig();

        // 同时设置一个定时器，在一定时间内检查配置
        const maxAttempts = 10;
        let attempts = 0;
        const interval = setInterval(() => {
            if (attempts >= maxAttempts || isIOSUpdating) {
                clearInterval(interval);
                return;
            }

            checkConfig();
            attempts++;
        }, 1000);

        // 监听 window.pwaConfig 的变化
        let lastConfig = JSON.stringify(window.pwaConfig || {});
        const configInterval = setInterval(() => {
            if (isIOSUpdating) {
                clearInterval(configInterval);
                return;
            }
            const currentConfig = JSON.stringify(window.pwaConfig || {});
            if (currentConfig !== lastConfig) {
                lastConfig = currentConfig;
                checkConfig();
            }
        }, 500);

        // 10秒后强制清理
        setTimeout(() => {
            clearInterval(interval);
            clearInterval(configInterval);
        }, 10000);
    };

    // 在组件挂载时检查
    onMounted(() => {
        checkPWAInstalled();

        // 监听display-mode变化
        window.matchMedia('(display-mode: standalone)').addEventListener('change', e => {
            isPWAInstalled.value = e.matches;
            if (e.matches) {
                // 当进入PWA模式时，也检查orientation配置
                // checkAndUpdateOrientation();
            }
        });

        // 为所有设备（包括非iOS）在PWA环境下检查orientation配置
        if (isPWAInstalled.value) {
            setTimeout(() => {
                // checkAndUpdateOrientation();
            }, 1000); // 延迟1秒确保配置已加载
        }

        // 启动 iOS 自动更新处理
        handleIOSAutoUpdate();
    });

    // 根据主题获取themeColor
    const getThemeColor = (): string => {
        const themeType = getTheme();
        return getThemeConfig(themeType).themeColor;
    };

    // 新增: 更新主题色 meta 标签
    const updateThemeColorMeta = (themeColor: string) => {
        // 更新 theme-color meta 标签
        let themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', themeColor);
        } else {
            themeColorMeta = document.createElement('meta');
            themeColorMeta.setAttribute('name', 'theme-color');
            themeColorMeta.setAttribute('content', themeColor);
            document.head.appendChild(themeColorMeta);
        }

        // 更新 msapplication-navbutton-color meta 标签
        let navbuttonColorMeta = document.querySelector('meta[name="msapplication-navbutton-color"]');
        if (navbuttonColorMeta) {
            navbuttonColorMeta.setAttribute('content', themeColor);
        } else {
            navbuttonColorMeta = document.createElement('meta');
            navbuttonColorMeta.setAttribute('name', 'msapplication-navbutton-color');
            navbuttonColorMeta.setAttribute('content', themeColor);
            document.head.appendChild(navbuttonColorMeta);
        }

        // 可选: 更新 apple-mobile-web-app-status-bar-style
        let statusBarStyleMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (statusBarStyleMeta) {
            // 保持 black-translucent，因为这允许状态栏显示但是半透明
            // 如果需要改变这个行为，可以在这里修改
        }
    };

    // 新增: 动态更新 manifest
    const updateManifest = async (config: PWAManifestConfig) => {
        console.log(config, '动态更新 manifest');
        try {
            // 验证必要的字段
            if (!config.name || !config.iconUrl) {
                console.error('缺少必要的配置字段:', config);
                return false;
            }

            const origin = window.location.origin;
            const iconUrl = config.iconUrl.startsWith('http') ? config.iconUrl : new URL(config.iconUrl, origin).href;
            const fullUrl = window.pwaurl || window.location.href;

            // 获取当前主题颜色
            const themeColor = getThemeColor();

            // 更新主题色 meta 标签
            updateThemeColorMeta(themeColor);

            // 更新 iOS 相关标签
            const updateIOSMeta = () => {
                // 更新图标 - 只通过特定ID选择元素，避免影响网站favicon
                const iconTags = ['apple-touch-icon', 'apple-touch-icon-precomposed', 'apple-touch-startup-image'];

                // 通过ID更新所有图标标签，避免通过选择器匹配到favicon
                iconTags.forEach(id => {
                    const element = document.getElementById(id);
                    if (element) {
                        element.setAttribute('href', iconUrl);
                        // 确保设置了尺寸
                        if (!element.hasAttribute('sizes')) {
                            element.setAttribute('sizes', '180x180');
                        }
                    }
                });

                // 更新应用名称 - 移除旧的标签并添加新的
                const nameMetas = ['apple-mobile-web-app-title', 'application-name'];

                nameMetas.forEach(name => {
                    // 移除旧的
                    document.querySelector(`meta[name="${name}"]`)?.remove();

                    // 添加新的
                    const meta = document.createElement('meta');
                    meta.setAttribute('name', name);
                    meta.setAttribute('content', config.name);
                    document.head.appendChild(meta);
                });

                // 更新 URL - 确保使用完整的 URL（包含参数）
                const completeUrl = window.pwaurl;
                console.log(completeUrl, 'completeUrl');
                if (completeUrl) {
                    // 移除旧的 starturl meta
                    document.querySelector('meta[name="msapplication-starturl"]')?.remove();

                    // 添加新的 starturl meta
                    const startUrlMeta = document.createElement('meta');
                    startUrlMeta.setAttribute('name', 'msapplication-starturl');
                    startUrlMeta.setAttribute('content', completeUrl);
                    document.head.appendChild(startUrlMeta);
                }
            };


            // const appStore = useAppStore();
            // const getPwaTemplate = computed(() => appStore.GetPwaTempConfig as any);

            // console.log(getPwaTemplate.value, 'getPwaTemplate');
            // const storeIconList = getPwaTemplate.value?.storeIconList || [];
            // const screenshots = storeIconList.map((item: any) => {
            //     return {
            //         src: item.url,
            //         sizes: '551x979',
            //         type: 'image/jpeg',
            //     };
            // });

            console.log(config, 'config11111');

            // const storeIconList = computed(() => {
            //     return getPwaTemplate.value?.storeIconList || [];
            // });

            // const screenshots = storeIconList.value.map((item: any) => {
            //     return {
            //         src: item.url,
            //         sizes: '551x979',
            //         type: 'image/jpeg',
            //     };
            // });


            // 创建新的 manifest 内容
            const manifest = {
                name: config.name,
                short_name: config.shortName || config.name,
                // description: config.description || config.name,
                theme_color: themeColor,
                background_color: themeColor,
                // background_color: config.backgroundColor || '#ffffff',
                start_url: window.pwaurl || '/',
                scope: '/',
                display: 'standalone',
                icons: [
                    {
                        sizes: 'any',
                        src: iconUrl,
                        type: 'image/png',
                    },
                    {
                        src: iconUrl,
                        sizes: '512x512', // 最大尺寸，用于启动画面
                        type: 'image/png',
                    },
                ],
                screenshots: config.screenshots || [],
                id: window.pwaurl || '/',
                orientation: 'any',
            };

            // 创建新的 Blob URL
            const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
            const manifestUrl = URL.createObjectURL(blob);

            // 更新 manifest 链接
            const manifestLink = document.getElementById('app-manifest') as HTMLLinkElement;
            if (manifestLink) {
                const oldHref = manifestLink.href;
                manifestLink.href = manifestUrl;
                if (oldHref && oldHref.startsWith('blob:')) {
                    URL.revokeObjectURL(oldHref);
                }
            }

            // 更新 iOS meta 标签
            updateIOSMeta();

            return true;
        } catch (error) {
            console.error('更新 manifest 失败:', error);
            return false;
        }
    };

    return {
        isInstalling,
        installProgress,
        isFinished,
        canInstallPWA,
        pwaFailCount,
        setupPWAListener,
        startInstallAnimation,
        finishInstallAnimation,
        isPWAInstalled,
        checkPWAInstalled,
        updateManifest,
    };
}
